import { useCallback } from "react";
import { TellescopeModelFromName, useTellescopeMutation, useTellescopeQueryClient } from "@tellescope/tanstack-query";
import { resolveBlockRef } from "../utils";

export interface EnsureBlockIdArgs {
  viewId: string;
  blockId?: string;
  blockIndex: number;
}

export const useEnsureBlockId = () => {
  const { getQueryData } = useTellescopeQueryClient();

  const updateViewMutation = useTellescopeMutation({
    model: "custom_dashboards",
    method: "updateOne",
    optimisticUpdates: true,
    autoCacheUpdateStrategy: "update",
  });

  return useCallback(
    async ({ viewId, blockId, blockIndex }: EnsureBlockIdArgs): Promise<string | undefined> => {
      const blocks = getQueryData({ model: "custom_dashboards", method: "getOne", request: viewId })?.blocks;
      const { block, index } = resolveBlockRef({ blocks, blockId, blockIndex });
      if (!blocks || !block || index === -1) return undefined;

      const existingId = block.info?.id;
      if (existingId !== undefined) return existingId;

      const stampedId = crypto.randomUUID();
      const updatedBlock: TellescopeModelFromName<"custom_dashboards">["blocks"][number] = {
        ...block,
        info: { ...block.info, id: stampedId },
      };

      await updateViewMutation.mutateAsync({
        id: viewId,
        blocks: [...blocks.slice(0, index), updatedBlock, ...blocks.slice(index + 1)],
        _options: { replaceObjectFields: true },
      });

      return stampedId;
    },
    [getQueryData, updateViewMutation]
  );
};
